import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { mainExploreSalaryRoute } from 'modules/main/main.routes';
import { hideModal, showModal } from 'modules/main/store/ducks/modal.actions';
import { useCurrentModalType } from 'modules/main/store/hooks/useCurrentModalType';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { userDashboardApiSavePostingIntention } from 'modules/userDashboard/api/userDashboardApiSaveIntakeIntents';
import { deleteCookie, getCookieValue } from 'utils/cookies';
import { inNoninterruptibleUserFlow } from 'utils/intakeintents';
import { getFirstParam } from 'utils/url';

// Only users created after this date should see
// the intake intents modal
const INTAKE_INTENTS_START_DATE = new Date('2023-08-15');

// Routes that should not have user modals appear
const DISABLE_USER_MODAL_ROUTES = [mainExploreSalaryRoute.fullPath] as string[];

export function useShowUserModals() {
  const { user, isUserLoaded } = useUserEnvironment();

  const location = useLocation();

  const currentModalType = useCurrentModalType();

  const dispatch = useMainDispatch();

  const {
    created,
    email,
    emailVerified,
    firstName,
    intakeIntentsDatetimeSet,
    lastName,
    orgs,
    showNotifPrefModal,
  } = user || {};

  const verify = useCallback(() => {
    const justSignedUpModalCookie = getCookieValue('justSignedUpModal');
    const userDismissedNotificationPreferencesCookie = getCookieValue(
      'userDismissedNotificationPreferencesDialog',
    );

    const isAccountSettingsPage =
      location.pathname === '/dashboard/account-settings';
    const isLoginOrSignupPage = ['signup', '/login'].includes(
      location.pathname,
    );
    const isIOSMobileApp = window.navigator.userAgent.includes(
      'Idealist Mobile App',
    );
    const noModalPresent = !currentModalType;
    const informationModalPresent =
      currentModalType === 'INFORMATION_REQUIRED' && noModalPresent;

    // INFORMATION_REQUIRED expected for case of Facebook user not allowing
    // their email address and then has just filled one in.
    const emailNeedsVerification =
      Boolean(email) &&
      !emailVerified &&
      !justSignedUpModalCookie &&
      (noModalPresent || informationModalPresent);

    const informationRequired =
      (!firstName || !lastName || !email) &&
      !isAccountSettingsPage &&
      noModalPresent;

    const showSignupModal =
      isIOSMobileApp &&
      !isLoginOrSignupPage &&
      noModalPresent &&
      !user &&
      isUserLoaded;

    const showIntakeIntentModal =
      created &&
      new Date(created) > INTAKE_INTENTS_START_DATE &&
      !getFirstParam(window.location, 'tmpId') &&
      !intakeIntentsDatetimeSet &&
      noModalPresent &&
      !inNoninterruptibleUserFlow(location.pathname);

    const shouldFinishOAuth = justSignedUpModalCookie && noModalPresent;

    const showUpdateNotificationPreferencesModal =
      showNotifPrefModal &&
      noModalPresent &&
      !userDismissedNotificationPreferencesCookie;

    if (showSignupModal) {
      dispatch(showModal('SIGNUP', { uncloseable: true }));
      return;
    }

    if (emailNeedsVerification) {
      dispatch(showModal('VERIFY_EMAIL', {}));
      return;
    }

    // If verification email persists in another browser tab after
    // verification, close it
    // @NOTE: Polling occurs in VerifyEmailModal
    if (currentModalType === 'VERIFY_EMAIL' && emailVerified) {
      dispatch(hideModal());
      return;
    }

    if (informationRequired) {
      dispatch(showModal('INFORMATION_REQUIRED', {}));
      return;
    }

    if (shouldFinishOAuth) {
      if (
        ['Google', 'Facebook', '"Apple ID"'].includes(justSignedUpModalCookie)
      ) {
        dispatch(
          showModal('FINISH_OAUTH_SIGNUP', {
            onClose: () => deleteCookie('justSignedUpModal'),
          }),
        );
      } else {
        deleteCookie('justSignedUpModal');
      }

      return;
    }

    if (showUpdateNotificationPreferencesModal) {
      dispatch(showModal('NOTIFICATION_PREFERENCES', {}));
      return;
    }

    if (showIntakeIntentModal) {
      // If an org has signed up via post a listing,
      // automatically save their posting intention
      // once they have created an organization
      if (orgs && orgs.length > 0) {
        userDashboardApiSavePostingIntention({
          intakeIntents: ['POST_JOB_OR_OTHER_OPP'],
          intakeIntentSomethingElse: '',
        });
      } else {
        dispatch(showModal('INTAKE_INTENTS', {}));
      }
    }
  }, [
    location.pathname,
    firstName,
    lastName,
    user,
    isUserLoaded,
    email,
    currentModalType,
    emailVerified,
    showNotifPrefModal,
    intakeIntentsDatetimeSet,
    dispatch,
    created,
    orgs,
  ]);

  useEffect(() => {
    const isIOSMobileApp = window.navigator.userAgent.includes(
      'Idealist Mobile App',
    );

    if (
      (!user && !isIOSMobileApp) ||
      DISABLE_USER_MODAL_ROUTES.includes(location.pathname)
    )
      return;

    verify();
  }, [verify, user, location.pathname]);
}
